// NewRepoDialog —— 新建仓库对话框(对齐 docs/ui_dialog 规范)
// 由 DashboardPage 内联使用;创建成功后回调 onCreated(关闭并打开仓库详情)。
import { useState, type FormEvent } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Input,
  Label,
  Textarea,
  Radio,
  RadioGroup,
  Spinner,
  Text,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogActions,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { LockClosedRegular, GlobeRegular } from '@fluentui/react-icons';
import { reposApi } from '@/api/repos';
import type { PublicRepo } from '@/api/types';
import { useAuthStore } from '@/store/auth';
import { explainError } from '@/api/client';
import { HelpPopover } from '@/components/HelpPopover';
import { DialogCloseTitle } from '@/components/DialogCloseTitle';
import { toast } from '@/lib/toast';

const NAME_RE = /^[a-z0-9._-]+$/;

const useStyles = makeStyles({
  field: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: tokens.spacingVerticalL,
    gap: tokens.spacingVerticalXS,
  },
  fieldError: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  descriptionRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
  },
});

type FieldErrors = {
  name?: string;
  description?: string;
};

export function NewRepoDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (repo: PublicRepo) => void;
}) {
  const styles = useStyles();
  const user = useAuthStore((s) => s.user);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'private'>('private');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});

  function reset() {
    setName('');
    setDescription('');
    setVisibility('private');
    setError(null);
    setErrors({});
  }

  function validate() {
    const e: FieldErrors = {};
    if (!name.trim()) {
      e.name = '请输入仓库名';
    } else if (name.length < 3 || name.length > 39) {
      e.name = '3-39 字符';
    } else if (!NAME_RE.test(name)) {
      e.name = '只允许小写字母、数字、_、-、.';
    } else if (/^[-.]/.test(name)) {
      e.name = '不能以 - 或 . 开头';
    }
    if (description.length > 500) {
      e.description = '最多 500 字符';
    }
    setErrors(e);
    return !e.name && !e.description;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setError(null);
    setSubmitting(true);
    try {
      const repo = await reposApi.create({
        name: name.trim(),
        description: description || undefined,
        visibility,
      });
      toast.success(`仓库 ${repo.name} 创建成功`);
      onCreated(repo);
    } catch (err) {
      const msg = explainError(err);
      console.error(msg);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      modalType="alert"
      open={open}
      onOpenChange={(_, d) => {
        if (!d.open) {
          reset();
          onClose();
        }
      }}
    >
      <DialogSurface style={{ width: 560, maxWidth: '92vw' }}>
        <DialogBody>
          <DialogCloseTitle title="新建仓库" onClose={() => { reset(); onClose(); }} />
          <DialogContent>
            {error && (
              <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalL }}>
                <MessageBarBody>{error}</MessageBarBody>
              </MessageBar>
            )}
            <form onSubmit={handleSubmit} autoComplete="off">
              <div className={styles.field}>
                <Label htmlFor="repo-name" required>
                  仓库名 <HelpPopover content="仓库的唯一路径标识(位于 owner 之下)。3–39 位,仅限小写字母、数字、下划线(_)、连字符(-)和点(.),且不能以 - 或 . 开头。" />
                </Label>
                <Input
                  id="repo-name"
                  value={name}
                  onChange={(_, d) => setName(d.value)}
                  contentBefore={
                    user?.username ? (
                      <Text size={300}>{user.username}/</Text>
                    ) : undefined
                  }
                  placeholder="my-awesome-project"
                  autoFocus
                />
                {errors.name && <span className={styles.fieldError}>{errors.name}</span>}
              </div>

              <div className={styles.field}>
                <Label htmlFor="repo-description">
                  描述(可选) <HelpPopover content="一句话说明仓库的用途,最多 500 字,可留空。" />
                </Label>
                <Textarea
                  id="repo-description"
                  value={description}
                  onChange={(_, d) => setDescription(d.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="一句话说明这个仓库是干什么的"
                />
                <div className={styles.descriptionRow}>{description.length}/500</div>
                {errors.description && (
                  <span className={styles.fieldError}>{errors.description}</span>
                )}
              </div>

              <div className={styles.field}>
                <Label required>
                  可见性 <HelpPopover content="私有:仅你与协作者可见;公开:任何登录用户都能看到并克隆(取决于其权限)。" />
                </Label>
                <RadioGroup
                  value={visibility}
                  onChange={(_, d) => setVisibility(d.value as 'public' | 'private')}
                  layout="horizontal"
                >
                  <Radio
                    value="private"
                    label={
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <LockClosedRegular /> Private
                      </span>
                    }
                  />
                  <Radio
                    value="public"
                    label={
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <GlobeRegular /> Public
                      </span>
                    }
                  />
                </RadioGroup>
              </div>
            </form>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="primary"
              disabled={submitting}
              icon={submitting ? <Spinner size="tiny" appearance="inverted" /> : undefined}
              onClick={handleSubmit as any}
            >
              创建
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}